// src/features/quotation/components/QuotationFilter.jsx
import React, { useState } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  IconButton,
  Button,
  Popover,
  Grid,
  Typography,
} from "@mui/material";
import { Search, FilterList, Clear, Close } from "@mui/icons-material";
// ✅ Remove DatePicker import from @mui/material
// import { DatePicker } from '@mui/material'; // ❌ Wrong

// ✅ Import from @mui/x-date-pickers
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { QUOTATION_STATUS_LABELS } from "../types/quotationTypes";

const QuotationFilter = ({ filters = {}, onFilterChange, onClear }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [localFilters, setLocalFilters] = useState(filters);

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const handleFilterApply = () => {
    onFilterChange && onFilterChange(localFilters);
    handleFilterClose();
  };

  const handleFilterReset = () => {
    const emptyFilters = {
      status: "all",
      search: "",
      dateRange: { start: null, end: null },
      clientId: "all",
      minAmount: "",
      maxAmount: "",
      tags: [],
    };
    setLocalFilters(emptyFilters);
    onFilterChange && onFilterChange(emptyFilters);
    handleFilterClose();
  };

  const handleChange = (field, value) => {
    setLocalFilters((prev) => ({ ...prev, [field]: value }));
  };

  const hasActiveFilters = () => {
    const { status, search, clientId, minAmount, maxAmount, dateRange } =
      localFilters;
    return (
      status !== "all" ||
      search ||
      clientId !== "all" ||
      minAmount ||
      maxAmount ||
      dateRange?.start ||
      dateRange?.end
    );
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (localFilters.status !== "all") count++;
    if (localFilters.search) count++;
    if (localFilters.clientId !== "all") count++;
    if (localFilters.minAmount) count++;
    if (localFilters.maxAmount) count++;
    if (localFilters.dateRange?.start) count++;
    if (localFilters.dateRange?.end) count++;
    return count;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          mb: 2,
          alignItems: "center",
        }}
      >
        <TextField
          size="small"
          placeholder="Search quotations..."
          value={localFilters.search || ""}
          onChange={(e) => {
            handleChange("search", e.target.value);
            onFilterChange &&
              onFilterChange({ ...localFilters, search: e.target.value });
          }}
          sx={{ flex: 1, minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: localFilters.search && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    handleChange("search", "");
                    onFilterChange &&
                      onFilterChange({ ...localFilters, search: "" });
                  }}
                >
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={localFilters.status || "all"}
            onChange={(e) => {
              handleChange("status", e.target.value);
              onFilterChange &&
                onFilterChange({ ...localFilters, status: e.target.value });
            }}
            label="Status"
          >
            <MenuItem value="all">All Status</MenuItem>
            {Object.entries(QUOTATION_STATUS_LABELS).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant={hasActiveFilters() ? "contained" : "outlined"}
          startIcon={<FilterList />}
          onClick={handleFilterClick}
          size="small"
          sx={{
            background: hasActiveFilters()
              ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              : "none",
          }}
        >
          Filters
          {hasActiveFilters() && (
            <Chip
              label={getActiveFilterCount()}
              size="small"
              sx={{
                ml: 1,
                bgcolor: "white",
                color: "#667eea",
                height: 20,
                fontSize: "11px",
              }}
            />
          )}
        </Button>

        {hasActiveFilters() && (
          <Button
            size="small"
            startIcon={<Clear />}
            onClick={() => {
              const emptyFilters = {
                status: "all",
                search: "",
                dateRange: { start: null, end: null },
                clientId: "all",
                minAmount: "",
                maxAmount: "",
                tags: [],
              };
              setLocalFilters(emptyFilters);
              onClear && onClear();
            }}
          >
            Clear All
          </Button>
        )}

        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleFilterClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          PaperProps={{
            sx: { p: 3, width: 400, maxWidth: "90vw" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">Filter Quotations</Typography>
            <IconButton size="small" onClick={handleFilterClose}>
              <Close />
            </IconButton>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={localFilters.status || "all"}
                  onChange={(e) => handleChange("status", e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  {Object.entries(QUOTATION_STATUS_LABELS).map(
                    ([key, label]) => (
                      <MenuItem key={key} value={key}>
                        {label}
                      </MenuItem>
                    ),
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Min Amount"
                type="number"
                value={localFilters.minAmount || ""}
                onChange={(e) => handleChange("minAmount", e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₹</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Max Amount"
                type="number"
                value={localFilters.maxAmount || ""}
                onChange={(e) => handleChange("maxAmount", e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₹</InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <DatePicker
                label="Start Date"
                value={localFilters.dateRange?.start || null}
                onChange={(newValue) => {
                  handleChange("dateRange", {
                    ...localFilters.dateRange,
                    start: newValue,
                  });
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <DatePicker
                label="End Date"
                value={localFilters.dateRange?.end || null}
                onChange={(newValue) => {
                  handleChange("dateRange", {
                    ...localFilters.dateRange,
                    end: newValue,
                  });
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                  },
                }}
              />
            </Grid>
          </Grid>

          <Box
            sx={{ display: "flex", gap: 1, mt: 3, justifyContent: "flex-end" }}
          >
            <Button variant="outlined" onClick={handleFilterReset}>
              Reset
            </Button>
            <Button
              variant="contained"
              onClick={handleFilterApply}
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              }}
            >
              Apply Filters
            </Button>
          </Box>
        </Popover>

        {/* Active filter chips */}
        {hasActiveFilters() && (
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", ml: 1 }}>
            {localFilters.status !== "all" && (
              <Chip
                label={`Status: ₹{QUOTATION_STATUS_LABELS[localFilters.status]}`}
                onDelete={() => {
                  handleChange("status", "all");
                  onFilterChange &&
                    onFilterChange({ ...localFilters, status: "all" });
                }}
                size="small"
              />
            )}
            {localFilters.search && (
              <Chip
                label={`Search: ₹{localFilters.search}`}
                onDelete={() => {
                  handleChange("search", "");
                  onFilterChange &&
                    onFilterChange({ ...localFilters, search: "" });
                }}
                size="small"
              />
            )}
            {localFilters.minAmount && (
              <Chip
                label={`Min: ₹₹{localFilters.minAmount}`}
                onDelete={() => {
                  handleChange("minAmount", "");
                  onFilterChange &&
                    onFilterChange({ ...localFilters, minAmount: "" });
                }}
                size="small"
              />
            )}
            {localFilters.maxAmount && (
              <Chip
                label={`Max: ₹₹{localFilters.maxAmount}`}
                onDelete={() => {
                  handleChange("maxAmount", "");
                  onFilterChange &&
                    onFilterChange({ ...localFilters, maxAmount: "" });
                }}
                size="small"
              />
            )}
            {localFilters.dateRange?.start && (
              <Chip
                label={`From: ₹{new Date(localFilters.dateRange.start).toLocaleDateString()}`}
                onDelete={() => {
                  handleChange("dateRange", {
                    ...localFilters.dateRange,
                    start: null,
                  });
                  onFilterChange &&
                    onFilterChange({
                      ...localFilters,
                      dateRange: { ...localFilters.dateRange, start: null },
                    });
                }}
                size="small"
              />
            )}
            {localFilters.dateRange?.end && (
              <Chip
                label={`To: ₹{new Date(localFilters.dateRange.end).toLocaleDateString()}`}
                onDelete={() => {
                  handleChange("dateRange", {
                    ...localFilters.dateRange,
                    end: null,
                  });
                  onFilterChange &&
                    onFilterChange({
                      ...localFilters,
                      dateRange: { ...localFilters.dateRange, end: null },
                    });
                }}
                size="small"
              />
            )}
          </Stack>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default QuotationFilter;
